import { useState, useContext, useEffect } from "react";
import { supabase } from "../supabase";
import { CartContext, AuthContext, BrandContext, CartUIContext } from "../contexts";
import { Btn, HeartIcon } from "../components/ui";
import ShareButton from "../components/ShareButton";
import { getCartStockState } from "../utils/cartStock";
import { familyFor, memberIds, valuesForVariant, variantForValues } from "../utils/variantFamilies";
import { useFavorites } from "../utils/favorites";


const selectStyle = { width: "100%", boxSizing: "border-box", padding: "8px 10px", background: "var(--surface2)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 3, fontFamily: "var(--font-mono)", fontSize: "13px" };
const labelStyle = { display: "block", fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--text-muted)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 };

const ProductView = ({ product: routeProduct, products = [], setSelectedProduct, setView }) => {
  const { cart, dispatch } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const b = useContext(BrandContext);
  const { setCartOpen } = useContext(CartUIContext);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [added, setAdded] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [restockSubmitted, setRestockSubmitted] = useState(false);
  const [restockLoading, setRestockLoading] = useState(false);
  const [restockError, setRestockError] = useState(false);
  const [variantId, setVariantId] = useState(routeProduct?.id);
  useEffect(() => { setVariantId(routeProduct?.id); }, [routeProduct?.id]);
  if (!routeProduct) return null;
  const product = products.find(row => row.id === variantId) || routeProduct;
  const favorited = isFavorite(product.id);
  const family = familyFor(product.id);
  const options = family ? memberIds(family).map(id => products.find(row => row.id === id)).filter(Boolean) : [];
  const axisValues = family?.axes ? valuesForVariant(family, product.id) : null;
  const selectVariant = id => {
    const row = products.find(candidate => candidate.id === id);
    if (!row) return;
    setVariantId(id);
    setSelectedProduct?.(row);
    setActiveImg(0);
    setRestockSubmitted(false);
    setRestockError(false);
    setView("product", { productId: id, replace: true });
  };
  const chooseAxisValue = (axisIndex, value) => {
    const values = axisValues.map((current, index) => index === axisIndex ? value : current);
    const variant = variantForValues(family, values);
    if (variant) selectVariant(variant.id);
  };
  const { soldOut, atStockLimit, canAdd } = getCartStockState(product, cart);
  const productImages = product.images?.length > 0 ? product.images : (product.image ? [product.image] : []);

  const handleAdd = () => {
    if (!canAdd) return;
    dispatch({ type: "ADD", product });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
    window.umami?.track("add-to-cart", { product: product.name, price: product.price });
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "48px 24px" }}>
      <button onClick={() => setView("shop")} style={{ color: "var(--text-dim)", fontFamily: "var(--font-mono)", fontSize: "12px", marginBottom: 32, display: "flex", alignItems: "center", gap: 6 }}>← Back to Shop</button>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 56 }} className="fade-up r-grid r-gap">
        <div className="product-image-col" style={{ borderRadius: "var(--radius)", border: "1px solid var(--border)", overflow: "hidden" }}>
          <div className="product-img-container" style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "var(--surface2)", height: 480 }}>
            <img src={productImages[activeImg] || ""} alt={product.name} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }} />
          </div>
          {productImages.length > 1 && (
            <div style={{ display: "flex", gap: 8, padding: 12, borderTop: "1px solid var(--border)", background: "var(--surface)", overflowX: "auto" }}>
              {productImages.map((url, i) => (
                <button key={i} onClick={() => setActiveImg(i)} style={{
                  width: 56, height: 56, borderRadius: "var(--radius)", flexShrink: 0,
                  border: `2px solid ${activeImg === i ? "var(--amber)" : "var(--border)"}`,
                  overflow: "hidden", padding: 0, cursor: "pointer",
                }}>
                  <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="product-info-col" style={{ paddingTop: 12 }}>
          <p style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--amber)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 8 }}>{product.category}</p>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
            <h1 style={{ fontSize: "38px", lineHeight: 1.1, marginBottom: 8 }}>{product.name}</h1>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <Btn variant="ghost" small onClick={() => toggleFavorite(product.id)} style={favorited ? { color: "var(--red)", borderColor: "var(--red)", flexShrink: 0 } : { flexShrink: 0 }}><HeartIcon filled={favorited} />{favorited ? "Favorited" : "Favorite"}</Btn>
              <ShareButton product={product} variant="button" />
            </div>
          </div>
          {product.subtitle && <p style={{ fontFamily: "var(--font-mono)", fontSize: "14px", color: "var(--text-muted)", marginBottom: 16 }}>{product.subtitle}</p>}
          {family?.axes ? (
            <div style={{ marginBottom: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {family.axes.map((axis, axisIndex) => (
                <div key={axis.name}>
                  <label htmlFor={`product-variant-${axis.name}`} style={labelStyle}>{axis.name}</label>
                  <select id={`product-variant-${axis.name}`} value={axisValues[axisIndex]} onChange={event => chooseAxisValue(axisIndex, event.target.value)} style={selectStyle}>
                    {axis.values.map(value => {
                      const values = axisValues.map((current, index) => index === axisIndex ? value : current);
                      const row = options.find(option => option.id === variantForValues(family, values)?.id);
                      const soldOut = row && row.stock != null && row.stock <= 0;
                      return <option key={value} value={value} disabled={axis.name === "Size" && soldOut}>{value}{axis.name === "Size" && row ? (soldOut ? " - sold out" : ` - $${row.price.toFixed(2)}`) : ""}</option>;
                    })}
                  </select>
                </div>
              ))}
            </div>
          ) : family && options.length > 0 ? (
            <div style={{ marginBottom: 24 }}>
              <label htmlFor="product-variant-Size" style={labelStyle}>Size</label>
              <select id="product-variant-Size" value={product.id} onChange={event => selectVariant(event.target.value)} style={selectStyle}>
                {options.map(option => <option key={option.id} value={option.id} disabled={option.stock != null && option.stock <= 0}>{option.subtitle || option.name}{option.stock != null && option.stock <= 0 ? " - sold out" : ` - $${option.price.toFixed(2)}`}</option>)}
              </select>
            </div>
          ) : null}
          <p style={{ color: "var(--text-dim)", fontSize: "15px", lineHeight: 1.7, marginBottom: 28 }}>{product.description}</p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 32, background: "var(--surface)", padding: 16, borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
            {[["SKU", product.sku], ["Weight", `${product.weight} lbs`], ["In Stock", `${product.stock} units`], ["Category", product.category]].map(([k, v]) => (
              <div key={k}>
                <p style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>{k}</p>
                <p style={{ fontFamily: "var(--font-mono)", fontSize: "14px", color: "var(--text)" }}>{v}</p>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 12 }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: "32px", color: soldOut ? "var(--text-muted)" : "var(--amber)" }}>${product.price.toFixed(2)}</span>
            {soldOut
              ? <span style={{ fontFamily: "var(--font-mono)", fontSize: "14px", color: "var(--red)", fontWeight: 700, letterSpacing: "0.08em" }}>SOLD OUT</span>
              : <Btn onClick={handleAdd} disabled={atStockLimit} style={{ flex: 1 }}>{added ? "✓ Added to Cart" : "Add to Cart"}</Btn>
            }
          </div>
          {!soldOut && atStockLimit && (
            <p style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)", marginBottom: 12 }}>
              That is all we have.{" "}
              <button
                type="button"
                onClick={() => setCartOpen(true)}
                style={{ background: "none", border: "none", padding: 0, font: "inherit", color: "var(--amber)", textDecoration: "underline", cursor: "pointer" }}
              >View cart</button>
            </p>
          )}
          {soldOut ? (
            <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 16, marginBottom: 12 }}>
              {restockSubmitted ? (
                <p style={{ fontFamily: "var(--font-mono)", fontSize: "12px", color: "var(--green)" }}>✓ We'll notify you when this product is back in stock.</p>
              ) : (
                <>
                  <p style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--text-muted)", letterSpacing: "0.08em", marginBottom: 8 }}>{restockError ? "Notification request could not be confirmed. Your email is kept here." : "GET NOTIFIED WHEN BACK IN STOCK"}</p>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      type="email"
                      value={user?.email || ""}
                      readOnly
                      aria-label="Account email for restock notifications"
                      placeholder={user?.email || "your@email.com"}
                      style={{ flex: 1, fontSize: "16px" }}
                    />
                    <Btn small disabled={restockLoading || !user?.email} onClick={async () => {
                      const email = user?.email;
                      if (!email) return;
                      setRestockLoading(true);
                      setRestockError(false);
                      try {
                        const { error } = await supabase.from("restock_waitlist").upsert({ product_id: product.id, email }, { onConflict: "product_id,email" });
                        if (error) throw error;
                      } catch {
                        setRestockError(true);
                        return;
                      } finally {
                        setRestockLoading(false);
                      }
                      supabase.functions.invoke("send-waitlist-confirmation", {
                        body: { productName: product.name },
                      }).catch(err => console.error("Waitlist confirmation email failed:", err));
                      setRestockSubmitted(true);
                    }}>{restockLoading ? "…" : "Notify Me"}</Btn>
                  </div>
                </>
              )}
            </div>
          ) : (
            <p style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)" }}>
              {b.freeShippingEnabled !== false && b.freeShippingThreshold > 0
                ? `Free shipping on orders over $${b.freeShippingThreshold} · Ships in 2 business days`
                : "Ships in 2 business days"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductView;
